import React, { useEffect, useState } from "react";
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Button } from "@mui/material";
import DatePickerV2ComponentNoTitle from "../DatePickerV2ComponentNoTitle";
import dayjs from "dayjs";
import { showCustomToast } from "../Misc/CustomToast";
import ButtonDeleteRed from "../Misc/ButtonDeleteRed";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { PointElement } from "chart.js";

function AvailabilityChart(props) {
  const [clients, setClients] = useState([]);
  const [chartType] = useState("bar");
  const [isLoading, setIsLoading] = useState(true);
  const [clientDisplayLimit, setClientDisplayLimit] = useState(10);
  const [, setTotalRecords] = useState(0);
  const [filteredClients] = useState([]);
  const [dateStart, setDateStart] = useState(Timestamp.fromDate(new Date())); // Aujourd'hui
  const [dateEnd, setDateEnd] = useState(
    Timestamp.fromDate(new Date(Date.now() + 21 * 24 * 60 * 60 * 1000))
  );
  const [presenceCounts, setPresenceCounts] = useState({});
  const [showAvailableRooms] = useState(true);
  const [, setClientDetails] = useState([]);
  const [showStatusSplit] = useState(false);
  const handleDateStartChange = (value) => {
    setDateStart(value);
  };
  const handleDateEndChange = (value) => {
    setDateEnd(value);
  };
  const resetDates = () => {
    setDateStart("");
    setDateEnd("");
  };

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
  );

  ChartJS.register({
    id: "warningIndicator",
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      chart.data.datasets.forEach((dataset, i) => {
        chart.getDatasetMeta(i).data.forEach((bar, index) => {
          const total = dataset.data[index];
          if (total > 25) {
            ctx.save();
            ctx.fillStyle = "red";
            ctx.fillRect(bar.x - bar.width / 2, bar.y, bar.width, 5);
            ctx.restore();
          }
        });
      });
    },
  });
  ChartJS.register(ChartDataLabels);

  const Histogram = ({ data }) => {
    const options = {
      responsive: true,
      scales: {
        x: {
          stacked: chartType === "bar",
          ticks: {
            callback: function (value, index, values) {
              const label = data.labels[index];

              return dayjs(label).format("DD/MM");
            },
          },
        },
        y: {
          stacked: chartType === "bar",
          max: 25,
        },
      },
      plugins: {
        datalabels: {
          display: (context) => {
            return (
              context.datasetIndex === context.chart.data.datasets.length - 1
            );
          },
          align: "end",
          anchor: "end",
          color: (context) => {
            if (
              showAvailableRooms &&
              context.dataset.data[context.dataIndex] === 0
            ) {
              return "red";
            } else {
              return "#444";
            }
          },
          font: {
            weight: (context) => {
              if (
                showAvailableRooms &&
                context.dataset.data[context.dataIndex] === 0
              ) {
                return "bold";
              } else {
                return "normal";
              }
            },
            size: 16,
          },
          formatter: (value, context) => {
            const total = context.chart.data.datasets.reduce((acc, dataset) => {
              return acc + (dataset.data[context.dataIndex] || 0);
            }, 0);
            return total;
          },
        },
        legend: {
          position: "top",
          labels: {
            paddingBottom: 20,
          },
        },
        title: {
          display: true,
          text: showAvailableRooms
            ? "Rooms available / day"
            : "Distribution / day",
          font: {
            size: 18,
          },
        },
      },
    };

    return chartType === "bar" ? (
      <Bar options={options} data={data} />
    ) : (
      <Line options={options} data={data} />
    );
  };

  useEffect(() => {
    setIsLoading(true);

    const fetchSpecificStatusClients = async () => {
      // Définir les statuts à requêter
      const statuses = [
        "In House",
        "Incoming",
        "Aftercare",
        "Potential",
        "Miracles@home",
      ];

      const queryRef = query(
        collection(db, "clients"),
        where("clientStatus", "in", statuses)
      );
      const snapshot = await getDocs(queryRef);
      let specificStatusClients = [];

      for (let docList of snapshot.docs) {
        let clientData = docList.data();
        clientData.id = docList.id;

        const assessmentDocRef = doc(
          db,
          "preAdmissionAssessments",
          clientData.id
        );
        const assessmentSnapshot = await getDoc(assessmentDocRef);
        if (assessmentSnapshot.exists()) {
          const assessmentData = assessmentSnapshot.data();
          clientData = { ...clientData, ...assessmentData };
        }

        specificStatusClients.push(clientData);
      }

      setClients(specificStatusClients);
      setTotalRecords(specificStatusClients.length);
      setIsLoading(false);
    };

    fetchSpecificStatusClients().catch((error) => {
      console.error("Failed to fetch clients with specific statuses:", error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (clients.length > 0 && dateStart && dateEnd) {
      handleGeneratePrevision();
    }
  }, [clients, dateStart, dateEnd]);

  const handleGeneratePrevision = () => {
    if (dateStart && dateEnd) {
      const newPresenceCounts = countPresencePerDay(
        clients,
        dateStart,
        dateEnd
      );

      setPresenceCounts(newPresenceCounts);
    } else {
      showCustomToast("Please select both start and end dates.", "error");
    }
  };

  useEffect(() => {
    const sortedClients = clients.sort((a, b) =>
      a.clientStatus.localeCompare(b.clientStatus)
    );

    const updatedClientDetails = sortedClients.map((client) => {
      const eta = client.dateOfAdmission
        ? dayjs(client.dateOfAdmission.toDate())
        : client.eta
        ? dayjs(client.eta.toDate())
        : null;
      const dischargeDate = client.actualDischargeDate
        ? dayjs(client.actualDischargeDate.toDate())
        : client.projectedResidentialDischargeDate
        ? dayjs(client.projectedResidentialDischargeDate.toDate())
        : eta
        ? eta.add(client.durationOfStay, "days")
        : null;
      let durationDays;

      if (eta && dischargeDate) {
        durationDays = dischargeDate.diff(eta, "day");
      }

      return {
        ...client,
        eta: eta ? eta.format("YYYY-MM-DD") : "N/A",
        endDate: dischargeDate ? dischargeDate.format("YYYY-MM-DD") : "N/A",
        durationDays: durationDays || "N/A",
      };
    });

    setClientDetails(updatedClientDetails);
  }, [clients]);

  const countPresencePerDay = (clients, dateStart, dateEnd) => {
    let presenceCounts = {};

    const startDateFilter = dayjs(dateStart.toDate());
    const endDateFilter = dayjs(dateEnd.toDate());

    let currentDate = startDateFilter;
    while (
      currentDate.isBefore(endDateFilter) ||
      currentDate.isSame(endDateFilter, "day")
    ) {
      const formattedDay = currentDate.format("YYYY-MM-DD");
      presenceCounts[formattedDay] = {
        Incoming: 0,
        Potential: 0,
        InHouse: 0,
        Aftercare: 0,
        "Miracles@home": 0,
      };
      currentDate = currentDate.add(1, "day");
    }

    clients.forEach((client) => {
      const eta = client.dateOfAdmission
        ? dayjs(client.dateOfAdmission.toDate())
        : client.eta
        ? dayjs(client.eta.toDate())
        : null;
      const dischargeDate = client.actualDischargeDate
        ? dayjs(client.actualDischargeDate.toDate())
        : client.projectedResidentialDischargeDate
        ? dayjs(client.projectedResidentialDischargeDate.toDate())
        : eta
        ? eta.add(client.durationOfStay, "days")
        : null;

      if (eta && dischargeDate) {
        for (
          let day = eta;
          day.isBefore(dischargeDate) || day.isSame(dischargeDate, "day");
          day = day.add(1, "day")
        ) {
          const formattedDay = day.format("YYYY-MM-DD");
          if (
            presenceCounts[formattedDay] &&
            (day.isAfter(startDateFilter) ||
              day.isSame(startDateFilter, "day")) &&
            (day.isBefore(endDateFilter) || day.isSame(endDateFilter, "day"))
          ) {
            const statusKey = client.clientStatus.replace(/\s/g, "");
            presenceCounts[formattedDay][statusKey] =
              (presenceCounts[formattedDay][statusKey] || 0) + 1;
          }
        }
      }
    });

    return presenceCounts;
  };

  const chartData = React.useMemo(() => {
    const labels = Object.keys(presenceCounts);

    if (showStatusSplit) {
      const inHouseData = labels.map(
        (day, index) =>
          presenceCounts[Object.keys(presenceCounts)[index]].InHouse || 0
      );
      const aftercareData = labels.map(
        (day, index) =>
          presenceCounts[Object.keys(presenceCounts)[index]].Aftercare || 0
      );

      return {
        labels,
        datasets: [
          {
            label: "InHouse",
            data: inHouseData,
            backgroundColor: "rgba(76, 175, 80, 0.7)",
          },
          {
            label: "Aftercare",
            data: aftercareData,
            backgroundColor: "rgba(255, 206, 86, 0.5)",
          },
        ],
      };
    } else {
      const availableRoomsData = labels.map((date) => {
        const totalPresence = Object.values(presenceCounts[date])
          .filter((value) => typeof value === "number")
          .reduce((sum, num) => sum + num, 0);
        return 25 - totalPresence;
      });

      return {
        labels,
        datasets: [
          {
            label: "Available Rooms",
            data: availableRoomsData,
            backgroundColor: "rgba(76, 175, 80, 0.7)",
          },
        ],
      };
    }
  }, [presenceCounts, showStatusSplit]);

  return (
    <div>
      <div
        style={{
          paddingLeft: "25px",
          width: "987px",
          display: "flex",
          flexDirection: "column",
          height: "20%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "430px",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: "10px",
            }}
          >
            Select range date:{" "}
          </span>
        </div>
        <div
          style={{
            width: "960px",
            paddingBottom: "20px",
            display: "flex",
            flexDirection: "row",
            alignItem: "center",
            justifyContent: "flex-start",
          }}
        >
          <div style={{ width: "200px" }}>
            <DatePickerV2ComponentNoTitle
              value={dateStart}
              onChange={handleDateStartChange}
            />
          </div>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: "10px",
              fontWeight: "500",
            }}
          >
            TO{" "}
          </span>
          <div style={{ width: "200px" }}>
            <DatePickerV2ComponentNoTitle
              value={dateEnd}
              onChange={handleDateEndChange}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              alignContent: "center",
              marginLeft: "10px",
              paddingTop: "10px",
            }}
          >
            <ButtonDeleteRed variant="contained" onClick={resetDates}>
              Reset Filters
            </ButtonDeleteRed>
          </div>
        </div>
      </div>

      <div
        style={{ height: "70vh", position: "relative", paddingLeft: "30px" }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "80%",
            }}
          >
            <CircularProgress />
            <p>Fetching data...</p>
          </Box>
        ) : (
          <div style={{ width: "80%" }}>
            {Object.keys(presenceCounts).length > 0 && (
              <div style={{ height: "80%", width: "100%" }}>
                <Histogram data={chartData} />
              </div>
            )}
          </div>
        )}
      </div>

      {!isLoading && filteredClients.length > clientDisplayLimit && (
        <div className="seemore-container">
          <Button
            onClick={() => setClientDisplayLimit(clientDisplayLimit + 10)}
          >
            See more
          </Button>
        </div>
      )}
    </div>
  );
}

export default AvailabilityChart;
